/* eslint-disable guard-for-in */
/* eslint-disable no-restricted-syntax */
/* eslint-disable no-nested-ternary */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { toast } from 'react-toastify';
import { DataTable } from 'primereact/datatable';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Column } from 'primereact/column';
import moment from 'moment';
import { ContentCopy, InfoOutlined, TextSnippet } from '@mui/icons-material';
import { Grid, IconButton, Tooltip } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { Paginator } from 'primereact/paginator';
import { SendStaticOrSingletonMethod } from '../sendStaticOrSingleMethod/sendStaticOrSingleMethod';
import {
  StyledAddDeviceToLocationButton,
  StyledIconStatus,
  StyledStatusDevice
} from './devicesTemplateStyle';
import { AddDeviceBar } from '../addDeviceBar/addDeviceBar';
import { RightSideBar } from '../../../components/layout/rightSideBar/rightSideBar';
import {
  StyledBoxTable,
  StyledColumnSize
} from '../../../components/layout/main/mainStyles';
import { useGetDevicesMutation } from '../../../services/device/deviceService';
import { viewRightSideBarEnum } from '../../../constants/sidebarEnums';
import { LoadingRowsComponent } from '../../../components/commons/LoadingRows/LoadingRows';
import Strings from '../../../i18n';
import { DetailsDevice } from '../detailsDevice/detailsDevice';
import { AddDeviceToLocation } from '../addDeviceToLocation/addDeviceToLocation';
import { DeleteButtonWithoutText } from '../../../components/commons/DeleteButtonWithoutText/deleteButtonWithoutText';
import { useDeleteDeviceFromLocationMutation } from '../../../services/deviceByLocation/deviceByLocationService';
import {
  DeviceByLocation,
  RegisteredDevice
} from '../../../types/Device/DeviceInterfaces';
import { DeviceTemplateProps } from './deviceTemplateProps';
import { MultipleDevicesSelectedMethods } from '../multipleDeviceSelectedMethods/multipleDevicesSelectedMethods';
import { SendDirectFOTA } from '../directFOTA/sendDirectFOTA';
import { ShowLastAnnounceInfo } from './showLastAnnounceInfo/showLastAnnounceInfo';
import { AddNoteSideBar } from '../../../components/commons/notes/addNoteSideBar/addNoteSideBar';
import { useDeleteScheduleFirmwareUpdateMutation } from '../../../services/firmwareVersions/firmwareVersionsService';
import { DeviceFiltersSideBar } from '../deviceFilters/deviceFiltersSideBar';
import { StyledButtonName } from '../../usersTemplate/userTemplateStyles';
import { colors } from '../../../constants/colors';
import { FirmwareStatusEnum } from '../../../constants/enums/firmwareEnums';
import { syncStatusEnum } from '../../../constants/enums/deviceEnums';
import { MoveDeviceToLocation } from '../moveDeviceToLocation/moveDeviceToLocation';
import { SendDirectMethod } from '../sendDirectMethod/sendDirectMethod';
import { SetWifiCredentialsComponent } from '../../locations/devicesByLocation/setWifiCredentials/setWifiCredentials';
import { SetProFlagSideBar } from '../setProFlagBar/setProFlagSideBar';
import { SendProdCommands } from '../sendProdCommands/sendProdCommands';
import { GetDeferEventTime } from '../getDeferEventTime/getDeferEventTime';
import { SetDeferEventTime } from '../setDeferEventTime/setDeferEventTime';
import { ResetFactoryControllers } from '../resetFactoryControllers/resetFactoryController';
import './devicesTemplate.css';

const flagIcon = require('../../../assets/proImage.png');

const ROW_HEIGHT = 35;
const TABLE_STYLE = { fontSize: '14px', tableLayout: 'fixed' } as const;

export const DevicesTemplate = ({
  searchValue,
  openRightSideBar,
  setOpenRightSideBar,
  sideBarOpenFor,
  setSideBarOpenFor,
  selectedDevices,
  setSelectedDevices,
  refreshDevices,
  setRefreshDevices
}: DeviceTemplateProps) => {
  const location = useLocation();
  const { search } = location;
  const queryParams = useMemo(() => new URLSearchParams(search), [search]);

  const totalItemsThreshold = 250;
  const [showFotaStatus, setShowFotaStatus] = useState('');
  const navigate = useNavigate();

  const page = queryParams.get('page');
  const [paginator, setPaginator] = useState({
    currentPage: page ? +page : 1,
    pageSize: totalItemsThreshold
  });

  const derivedFilters = useMemo(
    () => ({
      FilterByModel: queryParams.get('filterByModel') || null,
      filterByKeyword: queryParams.get('filterByKeyword') || null,
      FilterByUser: queryParams.get('filterByUser') || null,
      FilterByStatus: queryParams.get('filterByStatus') || null,
      FilterByLocationStatus: queryParams.get('filterByLocationStatus') || null,
      ShowPendingFotaDevices: queryParams.get('showPendingFotaDevices') || null,
      FilterByVersion: queryParams.get('filterByVersion') || null,
      search: queryParams.get('search') ?? null
    }),
    [queryParams]
  );

  const [filters, setFilters] = useState(derivedFilters);
  useEffect(() => {
    let changed = false;
    for (const k of Object.keys(
      derivedFilters
    ) as (keyof typeof derivedFilters)[]) {
      if (filters[k] !== derivedFilters[k]) {
        changed = true;
        break;
      }
    }
    if (changed) setFilters(derivedFilters);
  }, [derivedFilters]);

  const [allDevices, setAllDevices] = useState<RegisteredDevice[]>([]);
  const op = useRef<OverlayPanel>(null);
  const [addSingleDeviceLocation, setAddSingleDeviceLocation] = useState<
    RegisteredDevice | undefined
  >(undefined);

  const openRightSideBarForMethod = useCallback(
    (method: number) => {
      setSideBarOpenFor(method);
      setOpenRightSideBar(true);
    },
    [setOpenRightSideBar, setSideBarOpenFor]
  );

  const [getDevices, { isLoading, isSuccess, data }] = useGetDevicesMutation();
  const [deleteFromLocation, { isSuccess: isSuccessDeleteDeviceFromLocation }] =
    useDeleteDeviceFromLocationMutation();
  const [
    deleteScheduledFirmware,
    { isSuccess: isSuccessDeleteScheduledFirmware }
  ] = useDeleteScheduleFirmwareUpdateMutation();

  const clearSelectedDevices = useCallback(() => {
    setSelectedDevices([]);
    setAddSingleDeviceLocation(undefined);
  }, [setSelectedDevices]);

  const reloadTable = useCallback(() => {
    getDevices({
      page: paginator.currentPage,
      size: paginator.pageSize,
      additionalParams: filters
    });
    clearSelectedDevices();
  }, [
    getDevices,
    paginator.currentPage,
    paginator.pageSize,
    filters,
    clearSelectedDevices
  ]);

  useEffect(() => {
    reloadTable();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteDeviceFromLocation = useCallback(
    (item: RegisteredDevice) => {
      deleteFromLocation([item.id]);
    },
    [deleteFromLocation]
  );

  const openSideBarToAddDevice = useCallback(
    (item: RegisteredDevice) => {
      setAddSingleDeviceLocation(item);
      setOpenRightSideBar(true);
      setSideBarOpenFor(viewRightSideBarEnum.addDeviceToLocation);
    },
    [setOpenRightSideBar, setSideBarOpenFor]
  );

  useEffect(() => {
    if (isSuccessDeleteDeviceFromLocation) {
      toast.success('Device deleted from location successfully');
      reloadTable();
    }
  }, [isSuccessDeleteDeviceFromLocation, reloadTable]);

  useEffect(() => {
    if (refreshDevices) {
      reloadTable();
      setRefreshDevices(false);
    }
  }, [refreshDevices, reloadTable, setRefreshDevices]);

  useEffect(() => {
    const handle = setTimeout(() => {
      if (selectedDevices.length === 1) {
        openRightSideBarForMethod(viewRightSideBarEnum.details);
      } else if (selectedDevices.length > 1) {
        openRightSideBarForMethod(viewRightSideBarEnum.editMultipleDevices);
      } else {
        setSideBarOpenFor(viewRightSideBarEnum.empty);
        setOpenRightSideBar(false);
      }
    }, 0);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDevices]);

  useEffect(() => {
    const searchQuery = queryParams.get('search') || '';
    setFilters((prev) =>
      prev.search === searchQuery ? prev : { ...prev, search: searchQuery }
    );
  }, [queryParams]);

  const getLocationAssign = useCallback(
    (item: RegisteredDevice) =>
      item.locationId ? (
        <a href={`/locations/${item.locationId}/devices`}>
          {item.locationName}
        </a>
      ) : (
        <StyledAddDeviceToLocationButton
          onClick={() => openSideBarToAddDevice(item)}
        >
          {Strings.locations.assignLocation}
        </StyledAddDeviceToLocationButton>
      ),
    [openSideBarToAddDevice]
  );

  const getLastAnnounce = useCallback((rowData: RegisteredDevice) => {
    return (
      <ShowLastAnnounceInfo
        deviceId={rowData.deviceId}
        lastAnnounceDate={rowData.lastAnnounce}
      />
    );
  }, []);

  const getDatabaseLocal = useCallback((rowData: RegisteredDevice) => {
    if (rowData.dataBaseStatus) {
      return (
        <StyledStatusDevice status={rowData.connectionStatus}>
          {rowData.statusLabel}
        </StyledStatusDevice>
      );
    }
    return '';
  }, []);

  const getTargetWithDeleteButton = useCallback(
    (rowData: RegisteredDevice) => {
      if (!rowData.firmwareUpdate) return <span />;
      const isError =
        rowData.firmwareUpdate.status ===
          FirmwareStatusEnum.firmwareUpdateRequestFailed ||
        rowData.firmwareUpdate.status ===
          FirmwareStatusEnum.firmwareUpdateCancelledBySystem;

      return (
        <div className="d-flex align-items-center">
          <DeleteButtonWithoutText
            onDelete={() => {
              deleteScheduledFirmware([
                {
                  id: rowData.firmwareUpdate!.firmwareScheduleId,
                  firmwareversion: rowData.firmwareUpdate!.firmwareTarget
                }
              ]);
            }}
            id={rowData.id.toString()}
            titleConfirmation={
              Strings.serverAccess.scheduleFirmwareUpdate.deleteFirmwareTitle
            }
            textConfirmation={
              Strings.serverAccess.scheduleFirmwareUpdate.deleteFirmware
            }
          />
          {rowData.firmwareUpdate.firmwareTarget}
          <div className="d-inline-block">
            <Tooltip
              title={rowData.firmwareUpdate.status}
              placement="top"
              arrow
            >
              <IconButton
                size="small"
                onClick={(e) => {
                  op.current?.toggle(e);
                  setShowFotaStatus(rowData.firmwareUpdate!.status);
                }}
                color={isError ? 'error' : 'inherit'}
              >
                <InfoOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
            <OverlayPanel ref={op}>
              <p>{showFotaStatus}</p>
            </OverlayPanel>
          </div>
        </div>
      );
    },
    [deleteScheduledFirmware, showFotaStatus]
  );

  const redirectToNotes = useCallback(
    (item: RegisteredDevice) => {
      navigate('/my-notes', {
        state: { type: 'Devices', id: item.deviceId }
      });
    },
    [navigate]
  );

  const getDeviceName = useCallback(
    (rowData: RegisteredDevice) => {
      if (!rowData.hasNotes) return rowData.name;
      return (
        <Grid container>
          <Tooltip title={Strings.common.copyText} placement="top" arrow>
            <IconButton size="small" onClick={() => redirectToNotes(rowData)}>
              <TextSnippet fontSize="small" />
            </IconButton>
          </Tooltip>
          <StyledButtonName needsInformation={false}>
            {rowData.name}
          </StyledButtonName>
        </Grid>
      );
    },
    [redirectToNotes]
  );

  const getDeviceId = useCallback((item: RegisteredDevice) => {
    const handleCopy = () => {
      navigator.clipboard.writeText(item.deviceId);
      toast.success(Strings.common.copiedText);
    };
    return (
      <Grid container>
        <Tooltip title={Strings.common.copyText} placement="top" arrow>
          <IconButton size="small" onClick={handleCopy}>
            <ContentCopy fontSize="small" />
          </IconButton>
        </Tooltip>
        <StyledButtonName needsInformation={false}>
          {item.deviceId}
        </StyledButtonName>
      </Grid>
    );
  }, []);

  const getDetailsSyncStatus = useCallback(
    (details: Record<string, boolean | null>) => {
      const syncIssues: string[] = [];
      for (const key in details) {
        const val = details[key];
        if (val === false)
          syncIssues.push(`${key}: ${Strings.devices.notSynced}`);
        else if (val === null) syncIssues.push(`${key}: ${Strings.devices.na}`);
      }
      return syncIssues.length > 0
        ? syncIssues.join(', ')
        : Strings.devices.allSynced;
    },
    []
  );

  const getSyncStatus = useCallback(
    (rowData: RegisteredDevice) => {
      switch (rowData.syncStatus.status) {
        case syncStatusEnum.synched:
          return (
            <Tooltip title={Strings.devices.allSynced}>
              <StyledIconStatus
                color={colors.green}
                className="pi pi-check-circle"
              />
            </Tooltip>
          );
        case syncStatusEnum.notSynched:
          return (
            <Tooltip title={getDetailsSyncStatus(rowData.syncStatus.details)}>
              <StyledIconStatus
                color={colors.red}
                className="pi pi-times-circle"
              />
            </Tooltip>
          );
        case syncStatusEnum.noInfo:
          return (
            <Tooltip title={Strings.devices.na}>
              <StyledIconStatus
                color={colors.mediumGray}
                className="pi pi-question-circle"
              />
            </Tooltip>
          );
        default:
          return '';
      }
    },
    [getDetailsSyncStatus]
  );

  const getProFlag = useCallback((rowData: RegisteredDevice) => {
    return rowData.proFlag ? (
      <img src={flagIcon} alt="Pro Flag" width="30px" />
    ) : (
      ''
    );
  }, []);

  const onPageChange = useCallback(
    (event: any) => {
      const next = { currentPage: event.page + 1, pageSize: event.rows };
      setPaginator(next);

      getDevices({
        page: next.currentPage,
        size: next.pageSize,
        additionalParams: filters
      });
    },
    [getDevices, filters]
  );

  const footer = useMemo(() => {
    if (!data?.totalItems || data.totalItems <= totalItemsThreshold)
      return null;
    return (
      <div className="row">
        <div className="col-12">
          <Paginator
            first={(paginator.currentPage - 1) * paginator.pageSize}
            rows={paginator.pageSize}
            totalRecords={data?.totalItems || 0}
            rowsPerPageOptions={[50, 250, 500]}
            onPageChange={onPageChange}
          />
        </div>
      </div>
    );
  }, [
    data?.totalItems,
    onPageChange,
    paginator.currentPage,
    paginator.pageSize
  ]);

  useEffect(() => {
    getDevices({
      page: paginator.currentPage,
      size: paginator.pageSize,
      additionalParams: filters
    });
  }, [
    location.search,
    paginator.currentPage,
    paginator.pageSize,
    filters,
    getDevices
  ]);

  useEffect(() => {
    if (isSuccess && data) {
      setAllDevices(data.data);
    }
  }, [isSuccess, data]);

  useEffect(() => {
    if (data && queryParams.get('search')) {
      const searched = queryParams.get('search')!;
      setSelectedDevices(
        allDevices.filter((device) => device.deviceId.includes(searched))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDevices]);

  const selectedDeviceIds = useMemo(
    () => selectedDevices.map((d) => d.deviceId),
    [selectedDevices]
  );
  const selectedLocationIds = useMemo(
    () => selectedDevices.map((d) => d.locationId),
    [selectedDevices]
  );
  const selectedProdIds = useMemo(
    () =>
      selectedDevices.map((d) => ({
        deviceId: d.deviceId,
        productTypeId: d.productTypeId
      })),
    [selectedDevices]
  );
  const versionsForFilter = useMemo(
    () =>
      [...new Set(allDevices.map((item) => item.firmwareVersion))].map(
        (version) => ({ id: version, name: version })
      ),
    [allDevices]
  );

  const ComponentToShow = useMemo(() => {
    switch (sideBarOpenFor) {
      case viewRightSideBarEnum.singletonDevice:
        return (
          <SendStaticOrSingletonMethod
            deviceIds={selectedDeviceIds}
            reloadTable={reloadTable as any}
            method={viewRightSideBarEnum.singletonDevice}
            clearSelectedDevices={clearSelectedDevices}
            setOpenRightSideBar={setOpenRightSideBar}
          />
        );
      case viewRightSideBarEnum.staticDevice:
        return (
          <SendStaticOrSingletonMethod
            deviceIds={selectedDeviceIds}
            reloadTable={reloadTable as any}
            method={viewRightSideBarEnum.staticDevice}
            setOpenRightSideBar={setOpenRightSideBar}
            clearSelectedDevices={clearSelectedDevices}
          />
        );
      case viewRightSideBarEnum.setProFlag:
        return (
          <SetProFlagSideBar
            devicesIds={selectedDeviceIds}
            reloadTable={reloadTable}
            setOpenRightSideBar={setOpenRightSideBar}
            clearSelectedItems={clearSelectedDevices}
          />
        );
      case viewRightSideBarEnum.productionCommands:
        return (
          <SendProdCommands
            deviceIds={selectedDeviceIds}
            setOpenRightSideBar={setOpenRightSideBar}
            clearSelectedDevices={clearSelectedDevices}
          />
        );
      case viewRightSideBarEnum.getDeferEventTime:
        return (
          <GetDeferEventTime
            deviceIds={selectedDeviceIds}
            setOpenRightSideBar={setOpenRightSideBar}
            clearSelectedDevices={clearSelectedDevices}
          />
        );
      case viewRightSideBarEnum.resetFactoryController:
        return (
          <ResetFactoryControllers setOpenRightSideBar={setOpenRightSideBar} />
        );
      case viewRightSideBarEnum.setDeferEventTime:
        return (
          <SetDeferEventTime
            deviceIds={selectedDeviceIds}
            setOpenRightSideBar={setOpenRightSideBar}
            clearSelectedDevices={clearSelectedDevices}
          />
        );
      case viewRightSideBarEnum.addDevice:
        return (
          <AddDeviceBar
            openRightSideBarForMethod={openRightSideBarForMethod}
            reloadTable={reloadTable as any}
            setOpenRightSideBar={setOpenRightSideBar}
            openRightSideBar={openRightSideBar}
            clearSelectedDevices={clearSelectedDevices}
          />
        );
      case viewRightSideBarEnum.details:
        return (
          <DetailsDevice
            openRightSideBarForMethod={openRightSideBarForMethod}
            reloadTable={reloadTable}
            device={selectedDevices[0]}
            setOpenRightSideBar={setOpenRightSideBar}
            openRightSideBar={openRightSideBar}
          />
        );
      case viewRightSideBarEnum.addDeviceToLocation:
        return (
          <AddDeviceToLocation
            reloadTable={reloadTable}
            ids={
              addSingleDeviceLocation
                ? [addSingleDeviceLocation.id]
                : selectedDevices.map((item) => item.id)
            }
            setOpenRightSideBar={setOpenRightSideBar}
            clearSelectedDevices={clearSelectedDevices}
          />
        );
      case viewRightSideBarEnum.editMultipleDevices:
        return (
          <MultipleDevicesSelectedMethods
            reloadTable={reloadTable}
            openRightSideBarForMethod={openRightSideBarForMethod}
            selectedDevice={selectedDevices}
            openRightSideBar={openRightSideBar}
            setOpenRightSideBar={setOpenRightSideBar}
            clearSingleDevice={() => setAddSingleDeviceLocation(undefined)}
          />
        );
      case viewRightSideBarEnum.directFOTA:
        return (
          <SendDirectFOTA
            deviceIds={selectedProdIds}
            reloadTable={reloadTable as any}
            clearSelectedDevices={clearSelectedDevices}
            setOpenRightSideBar={setOpenRightSideBar}
          />
        );
      case viewRightSideBarEnum.addNote:
        return (
          <AddNoteSideBar
            closeSideBar={() => setOpenRightSideBar(false)}
            targetId={selectedDevices[0].deviceId}
            type="device"
          />
        );
      case viewRightSideBarEnum.filters:
        return (
          <DeviceFiltersSideBar
            reloadTable={reloadTable}
            setOpenRightSideBar={setOpenRightSideBar}
            openRightSideBar={openRightSideBar}
          />
        );
      case viewRightSideBarEnum.moveLocation:
        return (
          <MoveDeviceToLocation
            reloadTable={reloadTable}
            ids={selectedDeviceIds}
            setOpenRightSideBar={setOpenRightSideBar}
            clearSelectedDevices={clearSelectedDevices}
          />
        );
      case viewRightSideBarEnum.sendDirectMethodText:
        return (
          <SendDirectMethod
            deviceIds={selectedDeviceIds}
            setOpenRightSideBar={setOpenRightSideBar}
            clearSelectedDevices={clearSelectedDevices}
          />
        );
      case viewRightSideBarEnum.SetWifiCredentials:
        return (
          <SetWifiCredentialsComponent
            deviceIds={selectedDeviceIds}
            locationIds={selectedLocationIds}
            reloadTable={reloadTable}
            setOpenRightSideBar={setOpenRightSideBar}
            clearSelectedDevices={clearSelectedDevices}
            openRightSideBarForMethod={openRightSideBarForMethod}
          />
        );
      default:
        return (
          <AddDeviceBar
            openRightSideBarForMethod={openRightSideBarForMethod}
            reloadTable={reloadTable as any}
            device={selectedDevices[0]}
            setOpenRightSideBar={setOpenRightSideBar}
            openRightSideBar={openRightSideBar}
            clearSelectedDevices={clearSelectedDevices}
          />
        );
    }
  }, [
    sideBarOpenFor,
    selectedDevices,
    selectedDeviceIds,
    selectedLocationIds,
    selectedProdIds,
    versionsForFilter,
    openRightSideBar
  ]);

  useEffect(() => {
    if (isSuccessDeleteScheduledFirmware) {
      reloadTable();
      toast.success(Strings.serverAccess.scheduleFirmwareUpdate.deleteSuccess);
    }
  }, [isSuccessDeleteScheduledFirmware, reloadTable]);

  const handleSelectionChange = useCallback(
    (e: any) => {
      setSelectedDevices(e.value);
    },
    [setSelectedDevices]
  );

  const getFirmware = (item: DeviceByLocation) => {
    const xSeriesGen = 7;
    return (
      <Grid
        container
        xs={12}
        justifyContent="center"
        alignContent="center"
        alignItems="center"
      >
        <span>
          {item.firmwareVersion}
          {item.generation < xSeriesGen && ` (${item.wiFiFirmwareVersion})`}
        </span>
      </Grid>
    );
  };

  return (
    <StyledBoxTable style={{ width: '100%' }} className="mt-3">
      {isLoading && <LoadingRowsComponent />}

      {isSuccess && allDevices && (
        <DataTable
          value={allDevices}
          dataKey="id"
          selectionMode="multiple"
          selection={selectedDevices}
          onSelectionChange={handleSelectionChange}
          size="small"
          scrollable
          scrollHeight="flex"
          virtualScrollerOptions={{ itemSize: ROW_HEIGHT }}
          selectionPageOnly
          tableStyle={TABLE_STYLE}
          rowHover={false}
          sortMode="single"
          footer={footer}
        >
          <Column selectionMode="multiple" style={{ width: '40px' }} />
          <Column
            field="proFlag"
            header=""
            sortable
            body={getProFlag}
            style={{ width: '40px' }}
          />
          <Column
            field="statusLabel"
            header="Online / Offline"
            sortable
            body={getDatabaseLocal}
          />
          <Column field="name" header="Name" sortable body={getDeviceName} />
          <Column
            field="deviceId"
            header="Controller ID"
            sortable
            body={getDeviceId}
          />
          <Column field="productType" header="Model" sortable />
          <Column
            field="firmwareVersion"
            header="Firmware Version"
            sortable
            body={getFirmware}
          />
          <StyledColumnSize
            field="firmwareUpdate.firmwareTarget"
            header="Pending FOTA"
            minWidth="200px"
            sortable
            body={getTargetWithDeleteButton}
          />
          <Column
            field="lastAnnounce"
            header="Last Announce"
            sortable
            body={getLastAnnounce}
          />
          <Column
            field="locationName"
            header="Location"
            body={getLocationAssign}
            sortable
          />
          <Column
            field="syncStatus.status"
            header="Sync Status"
            sortable
            body={getSyncStatus}
          />
        </DataTable>
      )}

      <RightSideBar
        open={openRightSideBar}
        onMenuClose={() => {
          setOpenRightSideBar(false);
          setSideBarOpenFor(viewRightSideBarEnum.addDevice);
        }}
      >
        {ComponentToShow}
      </RightSideBar>
    </StyledBoxTable>
  );
};
